import {Component, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output} from '@angular/core';
import {AbstractControlDirective, FormControl, FormGroup, NgControl, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldControl, MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {Subject} from 'rxjs';

@Component({
    selector: 'app-common-date-range',
    standalone: true,
    imports: [MatFormFieldModule, MatDatepickerModule, ReactiveFormsModule, MatInputModule],
    providers: [{ provide: MatFormFieldControl, useExisting: CommonDateRangeComponent }],
    templateUrl: './common-date-range.component.html',
    styleUrl: './common-date-range.component.scss',
})
export class CommonDateRangeComponent implements OnInit, OnChanges {
    @Input() dateRange: string | null | undefined = null;
    @Input() disabled? = false;
    @Input() setTodayRange = false;
    @Output() dateRangeChanged = new EventEmitter<string | null>();

    rangeForm = new FormGroup({ startDate: new FormControl(''), endDate: new FormControl('') });

    // обязательные поля и методы класса MatFormFieldControl чтобы обернуть в mat-form-field
    readonly autofilled = false;
    readonly controlType = 'date-range';
    readonly disableAutomaticLabeling = false;
    readonly focused = false;
    private static nextId = 0;
    readonly id = `date-range-${CommonDateRangeComponent.nextId++}`;
    readonly ngControl: NgControl | AbstractControlDirective | null = null;
    readonly placeholder = '';
    readonly required = false;
    stateChanges = new Subject<void>();
    readonly userAriaDescribedBy = '';
    errorState = false;
    maxDate = this.toDateTimeLocalFormat(new Date());

    // ------------------------------

    // обязательные поля и методы класса MatFormFieldControl чтобы обернуть в mat-form-field

    get value() {
        const startDate = new Date(this.rangeForm.value.startDate || '').toISOString();
        const endDate = new Date(this.rangeForm.value.endDate || '').toISOString();
        return `${startDate},${endDate}`;
    }

    get empty(): boolean {
        return !this.rangeForm.controls.startDate.value && !this.rangeForm.controls.endDate.value;
    }

    get shouldLabelFloat(): boolean {
        return this.focused || !this.empty;
    }

    @HostBinding('class.floating')
    get shouldLabelFloatGetter(): boolean {
        return this.shouldLabelFloat;
    }

    onContainerClick(): void {}

    setDescribedByIds(): void {}

    // ------------------------------

    ngOnInit(): void {
        if (this.dateRange) {
            this.setRangeValues(this.dateRange);
        } else {
            this.setToday();
        }

        this.rangeForm.valueChanges.subscribe(() => {
            this.validateDateRange();
        });

        if (this.disabled) {this.rangeForm.disable();}
    }

    ngOnChanges(val: any): void {
        const dateRange = val.dateRange;

        if (dateRange?.currentValue) {
            this.setRangeValues(dateRange.currentValue);
        } else if (dateRange?.currentValue === null) {
            this.rangeForm.reset({ startDate: '', endDate: '' }, { emitEvent: false });
            this.rangeForm.setErrors(null);
            this.dateRangeChanged.emit(null);
        }
    }

    // установка текущей даты при инициализации
    private setToday(): void {
        if(!this.setTodayRange) {
            this.rangeForm.reset({ startDate: '', endDate: '' }, { emitEvent: false });
            return;
        }

        const today = new Date();
        const startOfDay = new Date(today);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(today);

        this.rangeForm.patchValue(
            { startDate: this.toDateTimeLocalFormat(startOfDay), endDate: this.toDateTimeLocalFormat(endOfDay) },
            { emitEvent: false },
        );

        this.dateRangeChanged.emit(this.value);
    }

    // заполняет форму из входящей строки
    private setRangeValues(dateRange: string): void {
        if (typeof dateRange !== 'string' || !dateRange.includes(',')) {
            return;
        }
        const [start, end] = dateRange.split(',');
        const startPeriod = new Date(start.trim());
        const endPeriod = new Date(end.trim());

        if (this.isDateRangeValid(startPeriod, endPeriod)) {
            this.rangeForm.patchValue(
                { startDate: this.toDateTimeLocalFormat(startPeriod), endDate: this.toDateTimeLocalFormat(endPeriod) },
                { emitEvent: false },
            );

            this.rangeForm.setErrors(null);
            this.dateRangeChanged.emit(this.value);
        } else {
            this.rangeForm.setErrors({ invalidDateRange: true });
        }
    }

    private validateDateRange(): void {
        const startDate = this.rangeForm.value.startDate;
        const endDate = this.rangeForm.value.endDate;

        // Если оба поля пустые - валидно
        if (!startDate && !endDate) {
            this.rangeForm.setErrors(null);
            this.dateRangeChanged.emit(null);
            return;
        }

        // Если заполнено только одно поле - ошибка
        if ((!endDate || !startDate)) {
            this.rangeForm.setErrors({ incompleteDateRange: true });
            return;
        }

        const startPeriod = new Date(startDate);
        const endPeriod = new Date(endDate);

        if (this.isDateRangeValid(startPeriod, endPeriod)) {
            this.rangeForm.setErrors(null);
            this.dateRangeChanged.emit(this.value);
        } else {
            this.rangeForm.setErrors({ invalidDateRange: true });
        }
    }

    private isDateRangeValid(start: Date, end: Date): boolean {
        return start.getTime() < end.getTime();
    }

    private toDateTimeLocalFormat(date: Date): string {
        return (
            date.getFullYear() +
            '-' +
            String(date.getMonth() + 1).padStart(2, '0') +
            '-' +
            String(date.getDate()).padStart(2, '0') +
            'T' +
            String(date.getHours()).padStart(2, '0') +
            ':' +
            String(date.getMinutes()).padStart(2, '0')
        );
    }
}
